// Thin fetch wrapper – fügt JSON-Header und credentials für Cookies hinzu,
// wirft auf !ok mit dem Backend-Errortext.
export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });
  const ctype = res.headers.get('content-type') ?? '';
  const body: unknown = ctype.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body as { error?: string })?.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
};

export type Me = { username: string; role: 'admin' | 'user' };

export const auth = {
  me: () => api.get<Me>('/api/auth/me'),
  login: (username: string, password: string) => api.post<Me>('/api/auth/login', { username, password }),
  logout: () => api.post<{ ok: true }>('/api/auth/logout'),
};

export type AppMeta = {
  file?: string;
  importedAt?: string;
  sourceCount?: number;
};

export type StateSnapshot = {
  rows: unknown[];
  meta: AppMeta;
  capacityByTeam: Record<string, unknown>;
  planRange: Record<string, unknown>;
  releaseCal: Record<string, unknown>;
  history: unknown[];
  initialPlan: Record<string, unknown>;
  groupBy: 'release' | 'team' | 'assignee' | 'none';
  spToPtFactor: number;
};

// Deduplizierung für /api/state: verschiedene Stores (useData, useConfig)
// rufen beim Mount parallel diesen Endpoint auf. Ohne Dedupe würde der 46 MB
// große Payload mehrfach übertragen und gleichzeitig geparst → Browser hängt.
// Cache-Fenster: die Promise wird 500 ms lang wiederverwendet.
let _statePromise: Promise<StateSnapshot> | null = null;
function fetchStateOnce(): Promise<StateSnapshot> {
  if (_statePromise) return _statePromise;
  _statePromise = api.get<StateSnapshot>('/api/state');
  _statePromise
    .catch(() => { /* Fehler wird im Aufrufer verarbeitet */ })
    .finally(() => {
      // Nach kurzer Wartezeit den Cache freigeben, damit spätere manuelle
      // Refreshes wieder frische Daten holen.
      setTimeout(() => { _statePromise = null; }, 500);
    });
  return _statePromise;
}

export const state = {
  get:      () => fetchStateOnce(),
  summary:  () => api.get<{ issueCount: number }>('/api/state/summary'),
  saveRows: (rows: unknown[]) => api.post<{ ok: true; count: number }>('/api/rows', rows),
  saveConfig: (cfg: Partial<StateSnapshot>) => api.post<{ ok: true }>('/api/config', cfg),
};

// ─── Snapshot-Archiv ───────────────────────────────────────────────────

export type SnapshotArchiveItem = {
  id: number;
  createdAt: number;   // Unix-Timestamp Sekunden
  label: string;
  issueCount: number;
  meta: { file?: string; importedAt?: string; sourceCount?: number } | null;
};

export type SnapshotList = { max: number; items: SnapshotArchiveItem[] };

// ─── Übersetzung ───────────────────────────────────────────────────────

export type TranslateField = 'summary' | 'expectedBehaviour';
export type TranslateModel = 'qwen2.5:7b' | 'qwen2.5:14b';

export type TranslateHealth = { ok: boolean; models: string[]; error?: string };
export type TranslatePreview = {
  count: number;
  chars: number;
  perField: { field: TranslateField; count: number }[];
};

export const translate = {
  health:  () => api.get<TranslateHealth>('/api/translate/health'),
  preview: (fields: TranslateField[], team: string | null) =>
    api.post<TranslatePreview>('/api/translate/preview', { fields, team }),
  start:   (fields: TranslateField[], team: string | null, model: TranslateModel) =>
    api.post<{ jobId: string; total: number }>('/api/translate/start', { fields, team, model }),
  cancel:  (jobId: string) =>
    api.post<{ ok: true }>(`/api/translate/${jobId}/cancel`),
  streamUrl:   (jobId: string) => `/api/translate/${jobId}/stream`,
  downloadUrl: (jobId: string) => `/api/translate/${jobId}/download`,
};

export const snapshots = {
  list:     () => api.get<SnapshotList>('/api/snapshots'),
  create:   (label?: string) => api.post<{ ok: true; id: number }>('/api/snapshots', label ? { label } : {}),
  rename:   (id: number, label: string) => request<{ ok: true }>(`/api/snapshots/${id}`,
    { method: 'PATCH', body: JSON.stringify({ label }) }),
  remove:   (id: number) => request<{ ok: true }>(`/api/snapshots/${id}`, { method: 'DELETE' }),
  activate: (id: number) => api.post<{ ok: true; count: number }>(`/api/snapshots/${id}/activate`),
};
